import { useEffect, useState } from "react";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";
import { Chart, GoogleChartControl, GoogleChartWrapper, GoogleViz, ReactGoogleChartEvent, ReactGoogleChartProps } from "react-google-charts";
import { CircularProgress, Typography } from "@mui/material";


interface JournalOfInstitution {
    journal: string,
    journalId: string,
    requestedArticlesNumber: number,
    lastPublication: string
}

interface InstitutionSummaryJournalGraphProps {
    institutionId: string,
    institutionName: string,
    mode: string,    
    onBarGraphClick: (institutionalId: string, drilldownTitle: string, journalId: string) => void
}

function InstitutionSummaryJournalGraph({ institutionId, institutionName,  mode, onBarGraphClick }: InstitutionSummaryJournalGraphProps) {
    const [isLoading, setIsLoading] = useState(true)
    const [institutionalDashboardJournalRetractions, setInstitutionalDashboardJournalRetractions] = useState<any[][]>([]);
    const [institutionalMapping, setInstitutionalMapping] = useState<Map<string, string>>();
    const label = mode === "retractions" ? "Retractions" : "High and Medium Risk articles"

    const handleBarClick: (eventCallbackArgs: { chartWrapper: GoogleChartWrapper | null; controlWrapper?: GoogleChartControl | undefined; props: ReactGoogleChartProps; google: GoogleViz; eventArgs: any; }) => void =
        ({ chartWrapper }: { chartWrapper: GoogleChartWrapper | null; controlWrapper?: GoogleChartControl | undefined; props: ReactGoogleChartProps; google: GoogleViz; eventArgs: any; }) => {
            if (chartWrapper != null) {
                const chart = chartWrapper.getChart();
                if (!chart || typeof chart.getSelection !== "function") {
                    console.warn("Chart is not ready or doesn't support selection.");
                    return;
                }

                const selection = chart.getSelection();

                if (selection.length > 0) {
                    const selectedItem = selection[0];
                    const dataTable = chartWrapper.getDataTable();
                    if (dataTable != null) {

                        if (selectedItem.row != null) {
                            const rowData: (any)[] = [];
                            for (let i = 0; i < dataTable.getNumberOfColumns(); i++) {
                                rowData.push(dataTable.getValue(selectedItem.row, i));
                            }
                            const journalId = institutionalMapping?.get(rowData[0])                            
                            if(journalId) {
                                onBarGraphClick(institutionId, institutionName + " and " + rowData[0], journalId);
                            }                            
                        }
                    }
                }
            }
        };

    const chartEvents: ReactGoogleChartEvent[] = [
        {
            eventName: "select",
            callback: handleBarClick,
        },
    ];

    const journalColumnHeaders: any[][] = [
        [
            { type: "string", label: "Journal" },
            { type: "number", label: label }
        ]
    ]

    const apiPath = (mode == 'retractions') ? "journals-for-retractions" : "journals-for-high-medium-risk"

    useEffect(() => {

        setIsLoading(true)

        async function fetchData() {
            let authToken = await auth.currentUser?.getIdToken(true)

            fetch(argosApiEntryPoint + '/stats/institutional-summary/' + apiPath + '?institutionId=' + institutionId
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((data: JournalOfInstitution[]) => {

                    let numericalSummaries: any[][] =
                        data.map(journalStats =>
                            [
                                journalStats.journal,
                                journalStats.requestedArticlesNumber                                
                            ]
                        )
                    setInstitutionalDashboardJournalRetractions(journalColumnHeaders.concat(numericalSummaries.slice(0, 10)))                    
                    setInstitutionalMapping(new Map(
                        data.map(({ journal, journalId, requestedArticlesNumber }) => [journal, journalId])
                    ))
                    setIsLoading(false)
                }
                );
        }
        fetchData()
    }, [institutionId]);

    const journalChartOptions = {
        legend: { position: 'none' },
        hAxis: {
            title: label,
            minValue: 0,
        },
        vAxis: {
            title: "Journal",
        },
        height: 600,
        chartArea: {
            height: 500,
        }
        //colors: ['#D33F00', '#FF3B30', '#FF9500']
    };

    return <> {isLoading && <CircularProgress />}
        {!isLoading &&
            <>
            <Typography variant="body1">Click on the bar to drill down and see the articles</Typography>
            <Chart
                chartType="BarChart"
                width="100%"
                data={institutionalDashboardJournalRetractions}
                options={{ ...journalChartOptions, view: { columns: [1] } }}
                chartEvents={chartEvents}
            /></>
        }</>;
}

export default InstitutionSummaryJournalGraph